import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';

export interface HiddenExcelTable {
    id: number;
    name: string;
    fieldList: string[];
    countryId: number;
}

interface HiddenExcelContextType {
    tables: HiddenExcelTable[];
    setTables: (tables: HiddenExcelTable[]) => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    selectedTable: HiddenExcelTable | null;
    setSelectedTable: (table: HiddenExcelTable | null) => void;
    isEditMode: boolean;
    setIsEditMode: (edit: boolean) => void;
    isCreateMode: boolean;
    setIsCreateMode: (create: boolean) => void;
}

const HiddenExcelContext = createContext<HiddenExcelContextType | undefined>(undefined);

export const useHiddenExcelContext = () => {
    const context = useContext(HiddenExcelContext);
    if (!context) {
        throw new Error('useHiddenExcelContext must be used within a HiddenExcelProvider');
    }
    return context;
};

interface HiddenExcelProviderProps {
    children: ReactNode;
}

export const HiddenExcelProvider: React.FC<HiddenExcelProviderProps> = ({ children }) => {
    const [tables, setTables] = useState<HiddenExcelTable[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTable, setSelectedTable] = useState<HiddenExcelTable | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);

    const contextValue = useMemo(
        () => ({
            tables,
            setTables,
            isLoading,
            setIsLoading,
            selectedTable,
            setSelectedTable,
            isEditMode,
            setIsEditMode,
            isCreateMode,
            setIsCreateMode,
        }),
        [tables, isLoading, selectedTable, isEditMode, isCreateMode]
    );

    return <HiddenExcelContext.Provider value={contextValue}>{children}</HiddenExcelContext.Provider>;
};
